import {Dispatch, useCallback, useReducer, useState} from 'react';
import {get} from "lodash";
import {Dialog, Heading, IconButton, ListIcon, Pane, RefreshIcon} from "evergreen-ui";
import {CloseButton, LoadingSpinner, Paths, RelayConnection, ErrorDisplay} from "common";
import {timeAgo} from "../../common/util/date";
import {ImportUserRequestListAction, importUserRequestListReducer, initState} from "./importUserRequestListReducer";
import {UserImportRequestDetail} from "./UserImportRequestDetail";
import {gql, useQuery} from "@apollo/client";
import {RelayPagination, useRelayPagination} from "../../providerapp/pagination";
import {ImportUserRequestInfo} from "../domain/types";
import {USER_POPUP_HEIGHT} from "../component/const";

export const UserImportHistoryPopup = () => {
    const [showPopup, setShowPopup] = useState(false);
    const closePopup = useCallback(() => {
        setShowPopup(false);
    }, []);
    return (<>
        <button
            onClick={() => setShowPopup(true)}
            className='bg-white h-10 w-32 my-4 py-2 px-1 text-atlantis-200 border
                hover:bg-gray-200 border-atlantis-200 font-semibold disabled:opacity-50
                text-sm focus:outline-none focus:ring focus:border-atlantis-300 rounded'>
            <ListIcon display={'inline'} className='pr-1'/> Import History
        </button>
        <Dialog
            isShown={showPopup}
            header={
                <Pane
                    className="w-full flex justify-between">
                    <Heading size={600} fontWeight="bolder">Import History</Heading>
                    <CloseButton className="mr-2" onClick={closePopup}/>
                </Pane>
            }
            hasHeader={true}
            hasFooter={false}
            width={'70%'}
            containerProps={{
                maxHeight: USER_POPUP_HEIGHT,
                minHeight: 600,
                marginY: `calc((100vh - ${USER_POPUP_HEIGHT}px) / 2)`,
                overflowY: 'auto'
            }}
            onCloseComplete={closePopup}
        >
            <ImportUserRequestListPopup/>
        </Dialog>
    </>)
}

interface ImportUserRequestInfoColumn {
    name: string;
    attr: Paths<ImportUserRequestInfo>;
}

export const ImportUserRequestInfoListColumn: Readonly<ImportUserRequestInfoColumn[]> = [
    {name: 'Provider', attr: 'providerName'},
    {name: 'Created Date', attr: 'createdDate'},
    {name: 'Total Record', attr: 'total'},
    {name: 'In-Progress', attr: 'progressing'},
    {name: 'Created', attr: 'created'},
    {name: 'Updated', attr: 'updated'},
    {name: 'Failed', attr: 'failed'},
]

export const IMPORT_USER_REQUESTS = gql`
query userImportRequests($first: Int = 20, $after: String) {
    userImportRequests(
            first: $first
            after: $after
    ) {
        edges {
            cursor
            node {
                guid
                providerName
                createdDate
                total
                created
                updated
                failed
                progressing
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
}
`

type ImportUserRequestsResponse = RelayConnection<ImportUserRequestInfo> & { totalElements: number };
const ImportUserRequestListPopup = () => {
    const [{
        selected,
        showDetailModal,
        pagination,
        refetchFunc
    }, dispatch] = useReducer(importUserRequestListReducer, initState())

    const {
        loading,
        data,
        error,
        refetch
    } = useQuery<{ userImportRequests: ImportUserRequestsResponse }, {}>(
        IMPORT_USER_REQUESTS, {
            notifyOnNetworkStatusChange: true,
            variables: {
                ...pagination.toGraphQlParams(),
            },
            onCompleted: () => {
                dispatch({type: "SET_REFRESH_FUNC", refetchFunc: refetch})
            }
        }
    );

    let totalRequests: number = 0;
    if (data?.userImportRequests?.pageInfo?.endCursor !== null) {
        totalRequests = data?.userImportRequests?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalRequests);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading user import requests" />;
    }

    return <div>
        <div className="flex justify-end pb-4">
            <RelayPagination pageInfo={data?.userImportRequests.pageInfo} count={totalRequests}
                             onClick={onPaginationClick}/>
            <IconButton intent='success'
                        marginX={8}
                        icon={RefreshIcon}
                        iconSize={18}
                        disabled={loading}
                        onClick={() => refetchFunc && refetchFunc()}
            />
        </div>
        <div className="pb-4">
            <table className="table-fixed w-full">
                <thead>
                <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                    {
                        ImportUserRequestInfoListColumn.map((column) => (
                            <th
                                key={column.attr}
                                className={'font-light ' + (column.attr === 'providerName' ? 'pl-8 w-6/12' : 'w-1/12')}
                            >
                                <div className='flex items-center'>
                                    <div>
                                        {column.name}
                                    </div>
                                </div>
                            </th>
                        ))
                    }
                </tr>
                </thead>
                <tbody>
                {
                    !loading && data!.userImportRequests.edges
                        .map((edge) => edge.node)
                        .map((row) => (
                            <ImportUserHistoryRecordPreview key={row.createdDate} record={row} dispatch={dispatch}/>
                        ))
                }
                </tbody>
            </table>
        </div>
        <UserImportRequestDetail show={showDetailModal} importListDispatch={dispatch} selectedRequest={selected}/>
        {
            loading &&
            <div className="pt-8">
                <div className="w-full h-full flex flex-grow items-center">
                    <LoadingSpinner spacing={20} height={"100%"}/>
                </div>
            </div>
        }
    </div>
}
type ImportUserHistoryRecordPreviewProps = {
    record: ImportUserRequestInfo
    dispatch: Dispatch<ImportUserRequestListAction>;
}
const ImportUserHistoryRecordPreview = ({record, dispatch}: ImportUserHistoryRecordPreviewProps) => {
    const displayColumn = (column: ImportUserRequestInfoColumn) => {
        if (column.attr === "createdDate") {
            if (record.createdDate === "") {
                return <></>
            }
            return <>{timeAgo(get(record, column.attr))}</>
        }

        return <>{get(record, column.attr)}</>
    }
    const hasFailed = record.failed > 0

    return (
        <tr className={`border-b border-gray-200 relative hover:${hasFailed ? "bg-red-300" : "bg-gray-200"} ${hasFailed ? "bg-red-200" : ""}`}
            onClick={() => dispatch({type: "SET_SHOW_DETAIL", selected: record})}>
            {
                ImportUserRequestInfoListColumn.map((column) => (
                    <td
                        key={column.name}
                        className={'py-5 truncate' + (column.attr === 'providerName' ? ' pl-8 w-6/12' : 'w-1/12')}
                    >
                        {
                            displayColumn(column)
                        }
                    </td>
                ))
            }
        </tr>
    );
}
